import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import styled from 'styled-components';
import i18n from '@/settings/i18n';
import { sentryReportError } from '@/utils/sentryReportError';
import { stringify } from 'query-string';
import { parse } from 'qs';

import { PageRootWrapper } from '@/components/shared/PageRootWrapper';
import { Admin } from '@/types/admin';
import { adminApi, adminsApi } from '@/services/api';
import { pages } from '@/settings/pages';
import { Activation } from '../Presentation';
import { Loading } from '@/components/utils/Loading';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';

export const ActivationContainer: React.FC = () => {
  const { flashError } = useFlashMessenger();
  const [isLoading, setIsLoading] = useState(true);
  const [isDisable, setIsDisable] = useState(false);

  const params = useParams<{ adminId: string; token: string }>();
  const adminId = Number(params.adminId);
  const token = params.token;

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const title = t('pages.activation.index.title');

  const searchParams = parse(location.search, { ignoreQueryPrefix: true }) as {
    locale?: string;
  };

  useEffect(() => {
    const { locale } = searchParams;
    if (locale) {
      i18n.changeLanguage(locale);
      delete searchParams.locale;
      navigate(
        {
          pathname: location.pathname,
          search: stringify(searchParams),
        },
        { replace: true },
      );
    }
  }, [searchParams, location, navigate]);

  const handleError = useCallback(
    (err: any): void => {
      if (err instanceof AxiosError && err.response !== undefined) {
        if (err.response && err.response.status === 401) {
          navigate(pages.token.expired, {
            state: { errorCode: err.response.data.error },
          });
        } else if (
          err.code !== 'ECONNABORTED' &&
          err.response &&
          err.response.status === undefined
        ) {
          // timeout以外の原因でresponseがない場合
          // (サーバーが落ちる場合、ユーザー側のネットワークでblockされている場合があります)
          flashError(t('ui.error.network_error'));
        } else {
          sentryReportError(err);

          flashError(t('ui.error.unexpected'));
        }
      }
    },
    [flashError, navigate, t],
  );
  const onSubmit = useCallback(
    async (admin: Admin, password: string, token: string) => {
      try {
        setIsDisable(true);
        await adminsApi.activate({
          data: {
            admin,
            password,
            token,
          },
        });

        navigate(pages.activation.success);
      } catch (err) {
        setIsDisable(false);
        handleError(err);
      }
    },
    [handleError, navigate],
  );

  useEffect(() => {
    (async () => {
      try {
        await adminApi.confirmToken(token);
        setIsLoading(false);
      } catch (err) {
        handleError(err);
      }
    })();
  }, [handleError, token]);

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <PageRootWrapper
        title={title}
        headerTitle={t('text.activation.index.prompt')}
        breadcrumbItems={[{ label: title }]}
        containerType="normal"
      >
        <Message>
          {t('text.activation.index.attention')}
          <br />
          {t('text.activation.index.request')}
        </Message>
        <Activation
          adminId={adminId}
          token={token}
          isDisable={isDisable}
          onSubmit={onSubmit}
        />
      </PageRootWrapper>
    );
  }
};

const Message = styled.p`
  margin: 4em 0 2.5em;
`;
